import { PageRequest } from '@/core/request/PageRequest';
import { useServiceError } from '@/hooks';
import notificationError from '@/utils/functions/notificationError';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { DEFAULT_ORDER, DEFAULT_ROWS } from '.';
import { PurchaseErrors } from './Purchase.errors';
import {
  calculateTaxesPurchase,
  calculateTaxesPurchaseFreightInvoice,
  getImportPurchases,
  getImportPurchasesCsv,
  getLastConsumablesPurchases,
  getLastPartsPurchases,
  getPurchase,
  getPurchaseGrouped,
  getPurchaseXml,
  listPurchasesGroup,
  purchaseAuthorize,
  purchaseCancel,
  purchaseConfirmImport,
  purchaseCreate,
  purchaseCreateParts,
  purchaseDownloadAllXml,
  purchaseDownloadXml,
  purchaseGroupCreate,
  purchaseRemove,
  purchaseSendBoschXml,
  purchasesList,
  purchaseUpdate,
  purchaseUploadXmlFile,
} from './Purchase.service';

import {
  PurchaseCreateRequest,
  PurchaseUpdateRequest,
  PurchaseAuthorizeRequest,
  PurchaseGroupCreate,
  LastItemPurchasesRequest,
  LastConsumablePurchasesRequest,
} from './Purchase.types';
import { PurchaseGroupingQuery } from './PurchaseGroup/PurchaseGroup.types';
import { PurchaseImportConfirmRequest } from './purchaseImport/PurchaseImport.types';
import { PurchaseImportCsvRequest } from '@/components/Movements/PurchasesPartsLink';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { queryClient } from '@/Root';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { AxiosError } from 'axios';
import arrayBufferParse from '@/utils/functions/arrayBufferParse';

const QUERY_ID = 'purchases';

export function usePurchases(params?: PageRequest) {
  const query = useInfiniteQuery(
    [QUERY_ID, { ...params }],
    ({ pageParam }) => {
      const queryParams: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };
      return purchasesList(queryParams);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      retry: 1,
    },
  );

  useServiceError(query.error);

  return query;
}

export function usePurchase(id: number) {
  const query = useQuery(['purchase/detail', id], () =>
    id ? getPurchase(id) : undefined,
  );

  useServiceError(query.error);

  return query;
}

export function usePurchaseXml() {
  const mutation = useMutation(
    (purchase: { purchaseId?: number }) => getPurchaseXml(purchase),
    {
      onSuccess(data, params) {
        queryClient.setQueryData(['purchaseXml', params], data);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );

  return mutation;
}

export function usePurchaseCreate() {
  const intl = useIntl();

  const mutation = useMutation(
    (obj: PurchaseCreateRequest) => purchaseCreate(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'purchases.notifications.create.success.title',
          }),
          description: intl.formatMessage({
            id: 'purchases.notifications.create.success.description',
          }),
        });
      },

      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) => PurchaseErrors[err] || err),
        });
      },
    },
  );

  return mutation;
}

export function usePurchaseUpdate() {
  const queryChanel = useQueryChanel();
  const intl = useIntl();

  const mutation = useMutation(
    (obj: PurchaseUpdateRequest) => purchaseUpdate(obj),
    {
      onSuccess(_, data) {
        queryChanel({
          invalidateQueries: [
            ['purchase/detail', data.id],
            ['items-purchase-return'],
          ],
        });

        notification.success({
          message: intl.formatMessage({
            id: 'purchases.notifications.update.success.title',
          }),
          description: intl.formatMessage({
            id: 'purchases.notifications.update.success.description',
          }),
        });
      },

      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) => PurchaseErrors[err] || err),
        });
      },
    },
  );

  return mutation;
}

export function usePurchaseRemove() {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const mutation = useMutation((id: number) => purchaseRemove(id), {
    onSuccess() {
      queryClient.refetchQueries([QUERY_ID]);

      notification.success({
        message: intl.formatMessage({
          id: 'purchases.notifications.remove.success.title',
        }),
        description: intl.formatMessage({
          id: 'purchases.notifications.remove.success.description',
        }),
      });
    },

    onError(error) {
      notificationError(error, {
        translate: (errors) => errors.map((err) => PurchaseErrors[err] || err),
      });
    },
  });

  return mutation;
}

export function useLastConsumablesPurchases(
  params?: LastConsumablePurchasesRequest,
) {
  return useQuery(
    ['lastPurchases', params],
    () => getLastConsumablesPurchases({ ...params }),
    {
      enabled: !!params?.consumableId,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useLastPartsPurchases(params?: LastItemPurchasesRequest) {
  return useQuery(
    ['lastPurchases', params],
    () =>
      getLastPartsPurchases({
        ...params,
      }),
    {
      enabled: !!params?.partId,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function usePurchasesGrouping(params?: PurchaseGroupingQuery) {
  const query = useInfiniteQuery(
    [`${QUERY_ID}-grouping`, { ...params }],
    ({ pageParam }) => {
      const queryParams: PurchaseGroupingQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };
      return listPurchasesGroup(queryParams);
    },
    {
      enabled: !!(params?.companyId && params?.supplierId),
      getNextPageParam: (lastPage) => lastPage.nextToken,
      onSuccess(data) {
        const purchases = flatInfiniteData(data);

        if (!purchases.length) {
          notification.warning({
            message:
              'Não foram encontradas compras para realizar o agrupamento',
          });
        }
      },
    },
  );

  useServiceError(query.error);

  return query;
}

export function usePurchaseGroupingGenerate() {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: PurchaseGroupCreate) => purchaseGroupCreate(obj),
    {
      onSuccess() {
        queryClient.refetchQueries([QUERY_ID]);

        notification.success({
          message: 'Sucesso!',
          description: 'Compra gerada com sucesso!',
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function usePurchaseAuthorize() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: PurchaseAuthorizeRequest) => purchaseAuthorize(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'purchases.notifications.authorize.success.title',
          }),
          description: intl.formatMessage({
            id: 'purchases.notifications.authorize.success.description',
          }),
        });
        queryClient.invalidateQueries(['purchase/detail']);
      },

      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) => PurchaseErrors[err] || err),
        });
      },
    },
  );

  return mutation;
}

export function usePurchaseCancel() {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const mutation = useMutation(purchaseCancel, {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'purchases.notifications.canceled.success.title',
        }),
        description: intl.formatMessage({
          id: 'purchases.notifications.canceled.success.description',
        }),
      });
      queryClient.invalidateQueries(['purchase/detail']);
    },

    onError(error) {
      notificationError(error, {
        translate: (errors) => errors.map((err) => PurchaseErrors[err] || err),
      });
    },
  });

  return mutation;
}

export function useCalcTaxesPurchases() {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (purchaseId: number) => {
      return calculateTaxesPurchase(purchaseId);
    },
    {
      async onSuccess() {
        notification.success({
          message: 'Sucesso',
          description: 'Tributos da Compra calculado com sucesso!',
        });
        await queryClient.invalidateQueries('purchase/detail');
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useCalcTaxesFreightInvoicePurchases() {
  const queryClient = useQueryClient();
  const mutation = useMutation(calculateTaxesPurchaseFreightInvoice, {
    onSuccess() {
      notification.success({
        message: 'Sucesso',
        description: 'Tributos da Compra calculado com sucesso!',
      });

      queryClient.invalidateQueries(['purchase/detail']);
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useImportPurchases() {
  const query = useMutation(getImportPurchases, {
    onSuccess() {
      notification.success({
        message: 'Sucesso',
        description: 'Xml foi importado com sucesso!',
      });
    },

    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            PurchaseErrors[err] ||
            err.includes('The key entered is already registered for purchase')
              ? 'Xml já foi cadastro em outra compra'
              : err.includes(
                  'The CNPJ informed is incompatible with the registered company',
                )
              ? 'O CNPJ do destinatário do arquivo XML a ser importado não possui nenhum vinculo com as empresas cadastradas para esse usuario!'
              : err,
          ),
      });
    },
  });

  return query;
}

export function usePurchaseImportConfirm(file?: File) {
  const query = useMutation(
    (obj: PurchaseImportConfirmRequest) => purchaseConfirmImport(obj, file),
    {
      onSuccess() {
        notification.success({
          message: 'Sucesso',
          description: 'A compra foi gerada com sucesso!',
        });
      },

      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) => PurchaseErrors[err] || err),
        });
      },
    },
  );

  return query;
}

export function usePurchaseGrouped(id: number) {
  const query = useQuery(['purchase/grouped', id], () =>
    id ? getPurchaseGrouped(id) : undefined,
  );

  useServiceError(query.error);

  return query;
}

// PartsLink

export function useImportPurchasesCsv() {
  const intl = useIntl();
  const mutation = useMutation(
    (file: PurchaseImportCsvRequest) => getImportPurchasesCsv(file),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'purchases.notifications.create.success.title',
          }),
        });
      },
    },
  );
  useServiceError(mutation.error);
  return mutation;
}

export function usePurchaseSendBoschXml() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: purchaseSendBoschXml,
    onSuccess(_, purchaseId) {
      notification.success({ message: 'Iniciado o processo de envio' });

      queryChanel({ invalidateQueries: [['purchase/detail', purchaseId]] });
    },
    onError(error) {
      const dicionary: Record<string, string> = {
        'Error sending XML to Bosch.': 'Erro ao enviar o XML para Bosch.',
      };

      notificationError(error, {
        translate: (errors) => errors.map((err) => dicionary[err] || err),
      });
    },
  });
}

export function usePurchaseDownloadXml() {
  return useMutation({
    mutationFn: purchaseDownloadXml,
    onSuccess() {
      notification.success({ message: 'XML baixado com sucesso!' });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function usePurchaseDownloadAllXml() {
  return useMutation({
    mutationFn: purchaseDownloadAllXml,
    onSuccess() {
      notification.success({
        message: 'Arquivo zip dos XMLs baixado com sucesso!',
      });
    },
    onError(error) {
      const err = error as AxiosError<ArrayBufferLike>;

      if (err.response?.data) {
        interface ResponseError {
          code: string;
          message: string;
          timestamp: string;
        }

        const response = arrayBufferParse<ResponseError>(err.response.data);

        return notification.error({
          message: 'Download dos XMLs',
          description: response?.message,
        });
      }

      notificationError(error);
    },
  });
}

export function usePurchaseUploadXmlFile() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: purchaseUploadXmlFile,
    onSuccess(_, vars) {
      notification.success({
        message: 'Arquivo XML enviado com sucesso!',
      });
      queryChanel({
        invalidateQueries: [['purchase/detail', vars.purchaseId]],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function usePurchaseCreateParts() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: purchaseCreateParts,
    onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Peça(s) geradas e vinculadas com sucesso!',
      });

      queryChanel({ invalidateQueries: [['items-purchase']] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}
